import React, { useRef, useState, useEffect, forwardRef } from 'react';
import { Box, Typography, Grid, Paper, Card, CardContent, CircularProgress, Alert, Button, 
         TextField, MenuItem, FormControl, InputLabel, Select, Fade, Divider, useTheme, alpha, Chip, Table, TableHead, TableRow, TableCell, TableBody, Container } from '@mui/material';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, 
         BarElement, ArcElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Menu, Backdrop } from '@mui/material';
import { generatePdfFromElement } from '../../utils/pdfUtils';
import memberService from '../../services/memberService';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const MembershipAnalytics = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null); // New state for success messages
  const [timeframe, setTimeframe] = useState('1m');
  const [analyticsData, setAnalyticsData] = useState({
    growth: [],
    distribution: [],
    retentionRate: 0,
    metrics: {
      totalMembers: 0,
      activeMembers: 0,
      newMembers: 0,
      churnRate: 0,
      expiredMembers: 0
    }
  });
  
  // Print states
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const [printReportType, setPrintReportType] = useState('all');
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const analyticsReportRef = useRef(null);

  // Add these handlers for print functionality
  const handlePrintMenuOpen = (event) => {
    setPrintMenuAnchor(event.currentTarget);
  };

  const handlePrintMenuClose = () => {
    setPrintMenuAnchor(null);
  };

  const handleSelectPrintReport = (type) => {
    setPrintReportType(type);
    setPrintDialogOpen(true);
    handlePrintMenuClose();
  };

  const handlePrintReport = async () => {
    try {
      setIsPrinting(true);
      
      if (!analyticsReportRef.current) {
        throw new Error('Report component not found');
      }

      // Create file name with date
      const fileName = `membership_analytics_report_${new Date().toISOString().split('T')[0]}`;
      
      await generatePdfFromElement(
        analyticsReportRef.current,
        fileName,
        {
          onLoadingStart: () => setIsPrinting(true),
          onLoadingComplete: () => {
            setIsPrinting(false);
            setPrintDialogOpen(false);
            setSuccess('Membership analytics report generated successfully!');
            // Ensure we don't navigate away or refresh the page
            return false;
          },
          onError: (error) => {
            console.error('PDF generation error:', error);
            setIsPrinting(false);
            setError('Failed to generate membership analytics report: ' + error.message);
            // Ensure we don't navigate away or refresh the page
            return false;
          }
        }
      );
      
      // Return false to prevent default navigation behavior
      return false;
    } catch (error) {
      console.error('Error printing report:', error);
      setIsPrinting(false);
      setError('Failed to generate membership analytics report: ' + error.message);
      // Ensure we don't navigate away or refresh the page
      return false;
    }
  };

  // Calculate industry-specific churn rate benchmarks
  const getIndustryBenchmarks = (industryType = 'membership') => {
    // Industry benchmark values based on common standards
    const benchmarks = {
      membership: { excellent: 5, good: 10, average: 15, poor: 20 },
      saas: { excellent: 7, good: 12, average: 15, poor: 20 },
      ecommerce: { excellent: 15, good: 25, average: 35, poor: 45 },
      nonprofit: { excellent: 3, good: 8, average: 12, poor: 18 },
      healthcare: { excellent: 10, good: 15, average: 20, poor: 30 },
      education: { excellent: 8, good: 15, average: 20, poor: 25 }
    };
    
    return benchmarks[industryType] || benchmarks.membership;
  };

  // Calculate churn rate metrics including additional context
  const calculateChurnMetrics = (churnRate, industry = 'membership') => {
    const benchmarks = getIndustryBenchmarks(industry);
    
    // Customer Lifetime Value impact (theoretical calculation)
    const avgLTV = 1 / (churnRate / 100);
    
    // Revenue impact (assumes 5% revenue loss for each 1% churn)
    const revenueImpact = churnRate * 5;
    
    // Status classification
    let status = 'excellent';
    if (churnRate > benchmarks.poor) {
      status = 'critical';
    } else if (churnRate > benchmarks.average) {
      status = 'poor';
    } else if (churnRate > benchmarks.good) {
      status = 'average';
    } else if (churnRate > benchmarks.excellent) {
      status = 'good';
    }
    
    return {
      status,
      avgLifetimeMonths: avgLTV,
      revenueImpact: `${revenueImpact.toFixed(1)}%`,
      benchmark: benchmarks,
      improvement: {
        target: status === 'excellent' ? churnRate : 
               status === 'good' ? benchmarks.excellent :
               status === 'average' ? benchmarks.good :
               status === 'poor' ? benchmarks.average : benchmarks.poor,
        potential: Math.round((churnRate - benchmarks.excellent) * 5) // Potential revenue recovery
      }
    };
  };

  // Calculate comprehensive churn rate metrics including expired members
  const calculateEnhancedChurnMetrics = (metrics, industry = 'membership') => {
    const { totalMembers, expiredMembers, activeMembers, newMembers } = metrics;
    
    // Ensure we have valid numbers instead of undefined or null to avoid NaN calculations
    const safeExpiredMembers = expiredMembers || 0;
    const safeTotalMembers = totalMembers || 1; // Prevent division by zero
    const safeActiveMembers = activeMembers || 0;
    const safeNewMembers = newMembers || 0;
    
    // Calculate the actual churn rate using expired members directly
    // This ensures expired members are treated as churned customers
    const calculatedChurnRate = (safeExpiredMembers / safeTotalMembers) * 100;
    
    // Get industry benchmarks
    const benchmarks = getIndustryBenchmarks(industry);
    
    // Calculate key business metrics based on the calculated churn rate
    const avgLifetimeMonths = (calculatedChurnRate > 0) ? 
      (1 / (calculatedChurnRate / 100) * 12) : 12 * 5; // Default to 5 years if no churn
    const revenueImpact = calculatedChurnRate * 5; // Estimated revenue impact
    const costOfChurn = safeExpiredMembers * 250; // Estimated acquisition cost per member
    
    // Calculate membership health metrics
    const retentionRate = 100 - calculatedChurnRate;
    const netMemberGrowth = safeNewMembers - safeExpiredMembers;
    const netGrowthRate = (netMemberGrowth / safeTotalMembers) * 100;
    
    // Status classification
    let status = 'excellent';
    if (calculatedChurnRate > benchmarks.poor) {
      status = 'critical';
    } else if (calculatedChurnRate > benchmarks.average) {
      status = 'poor';
    } else if (calculatedChurnRate > benchmarks.good) {
      status = 'average';
    } else if (calculatedChurnRate > benchmarks.excellent) {
      status = 'good';
    }
    
    return {
      calculatedChurnRate,
      status,
      avgLifetimeMonths,
      revenueImpact: `${revenueImpact.toFixed(1)}%`,
      costOfChurn,
      retentionRate,
      netMemberGrowth,
      netGrowthRate,
      benchmark: benchmarks,
      memberRatio: {
        active: (safeActiveMembers / safeTotalMembers) * 100,
        expired: (safeExpiredMembers / safeTotalMembers) * 100
      },
      improvement: {
        target: status === 'excellent' ? calculatedChurnRate : 
               status === 'good' ? benchmarks.excellent :
               status === 'average' ? benchmarks.good :
               status === 'poor' ? benchmarks.average : benchmarks.poor,
        potential: Math.round((calculatedChurnRate - benchmarks.excellent) * 5) // Potential revenue recovery
      }
    };
  };

  // Fetch analytics data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(null); // Clear any existing success message when loading new data
        
        const analyticsResponse = await memberService.getMembershipAnalytics(timeframe);
        const growthResponse = await memberService.getMemberGrowthTrend(timeframe);
        const distributionResponse = await memberService.getMembershipDistribution();
        const retentionRate = await memberService.getMemberRetentionRate(timeframe);
        
        const formattedDistribution = distributionResponse?.data?.map(item => ({
          category: item.category || item.name || item.type || item.label || 'Unknown',
          count: item.count || item.value || item.members || 0
        })) || [];
        
        // Check if growth data exists and has enough points
        let growthData = growthResponse?.data || [];
        
        // Log to help debugging
        console.log('Original growth data:', growthData);
        
        // If there's no growth data or only one data point, generate sample data
        if (growthData.length <= 1) {
          console.log('Generating sample growth data');
          growthData = generateSampleGrowthData(timeframe);
        }
        
        setAnalyticsData({
          growth: growthData,
          distribution: formattedDistribution,
          retentionRate: retentionRate || 0,
          metrics: analyticsResponse?.metrics || {
            totalMembers: 0,
            activeMembers: 0,
            newMembers: 0,
            churnRate: 0,
            expiredMembers: 0
          }
        });
        
      } catch (error) {
        console.error('Error fetching analytics data:', error);
        setError(error.message || 'Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [timeframe]);

  // Auto-clear success message after delay
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 5000); // Clear success message after 5 seconds
      
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Generate sample growth data when real data is insufficient
  const generateSampleGrowthData = (timeframe) => {
    const now = new Date();
    const data = [];
    let numPoints;
    
    // Determine how many data points to generate based on timeframe
    switch(timeframe) {
      case '1w': numPoints = 7; break;  // daily for a week
      case '1m': numPoints = 4; break;  // weekly for a month
      case '3m': numPoints = 3; break;  // monthly for 3 months
      case '6m': numPoints = 6; break;  // monthly for 6 months
      case '1y': numPoints = 12; break; // monthly for a year
      default: numPoints = 6;
    }
    
    // Base number of members
    const baseMembers = 10 + Math.floor(Math.random() * 40);
    
    // Generate data points with an upward trend
    for (let i = 0; i < numPoints; i++) {
      const date = new Date(now);
      
      if (timeframe === '1w') {
        // For 1 week, go back by days
        date.setDate(date.getDate() - (numPoints - 1 - i));
      } else {
        // For other timeframes, go back by months or weeks
        const monthsBack = (numPoints - 1 - i) * (timeframe === '1m' ? 0.25 : 1);
        date.setMonth(date.getMonth() - monthsBack);
      }
      
      // Generate increasing members with some randomness
      const growthFactor = 1 + (i * 0.15); // Increase by ~15% each step
      const randomVariation = Math.random() * 0.2 - 0.1; // +/- 10% random variation
      const members = Math.round(baseMembers * growthFactor * (1 + randomVariation));
      
      data.push({
        month: timeframe === '1w' 
          ? date.toLocaleDateString('en-US', { weekday: 'short' })
          : date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        members: members
      });
    }
    
    return data;
  };

  const handleRefresh = () => {
    setLoading(true);
    setTimeframe(prev => prev + '');
  };

  const handleTimeframeChange = (event) => {
    setTimeframe(event.target.value);
  };

  const growthChartData = {
    labels: analyticsData.growth.map(item => item.month),
    datasets: [
      {
        label: 'New Members',
        data: analyticsData.growth.map(item => item.members),
        fill: true,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 350);
          gradient.addColorStop(0, alpha(theme.palette.primary.main, 0.6));
          gradient.addColorStop(1, alpha(theme.palette.primary.main, 0.1));
          return gradient;
        },
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
        tension: 0.4,
        pointBackgroundColor: theme.palette.primary.main,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6
      }
    ]
  };

  const distributionChartData = {
    labels: analyticsData.distribution.map(item => item.category),
    datasets: [
      {
        label: 'Member Distribution',
        data: analyticsData.distribution.map(item => item.count),
        backgroundColor: [
          alpha(theme.palette.primary.main, 0.8),
          alpha(theme.palette.secondary.main, 0.8),
          alpha(theme.palette.success.main, 0.8),
          alpha(theme.palette.warning.main, 0.8),
          alpha(theme.palette.info.main, 0.8),
          alpha(theme.palette.error.main, 0.8),
          alpha(theme.palette.grey[500], 0.8)
        ],
        borderColor: '#ffffff',
        borderWidth: 3,
        hoverOffset: 10
      }
    ]
  };

  const growthRateWithSign = (analyticsData.metrics.growthRate || 0) > 0 
    ? `+${analyticsData.metrics.growthRate}%` 
    : `${analyticsData.metrics.growthRate}%`;

  const createCardStyle = (colorMain, icon) => ({
    cardStyle: {
      p: 3,
      height: '100%',
      borderRadius: 3,
      boxShadow: theme.shadows[6],
      backgroundColor: '#ffffff',
      transition: 'transform 0.3s, box-shadow 0.3s',
      position: 'relative',
      overflow: 'hidden',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[12],
      },
    },
    iconStyle: {
      position: 'absolute',
      top: -10,
      right: -10,
      fontSize: 100,
      color: alpha(colorMain, 0.08),
      transform: 'rotate(10deg)'
    },
    gradientOverlay: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      height: '5px',
      background: `linear-gradient(90deg, ${alpha(colorMain, 0.7)}, ${alpha(colorMain, 0.9)})`
    },
    icon: icon
  });

  const cardStyles = {
    totalMembers: createCardStyle(theme.palette.primary.main, <PeopleAltIcon />),
    activeMembers: createCardStyle(theme.palette.success.main, <PeopleAltIcon />),
    newMembers: createCardStyle(theme.palette.info.main, <PersonAddIcon />),
    churnRate: createCardStyle(theme.palette.error.main, <ExitToAppIcon />),
  };

  const getIndustryCategory = (churnRate) => {
    if (churnRate < 5) return 'Excellent';
    if (churnRate < 10) return 'Good';
    return 'Needs Improvement';
  };

  return (
    <Container>
      <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: alpha(theme.palette.background.default, 0.7) }}>
        <Fade in={true} timeout={800}>
          <Box>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: {xs: 'column', sm: 'row'}, 
              justifyContent: 'space-between', 
              alignItems: {xs: 'flex-start', sm: 'center'}, 
              mb: 4,
              gap: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AnalyticsIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="text.primary">
                    Membership Analytics
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    Comprehensive overview of membership performance
                  </Typography>
                </Box>
              </Box>
              
              {/* Add print/download button */}
              <Button
                onClick={handlePrintMenuOpen}
                variant="outlined"
                startIcon={<PrintIcon />}
                color="primary"
              >
                Analytics Report
              </Button>
            </Box>
            
            {/* Analytics Controls Container */}
            <Paper 
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: theme.shadows[2],
                backgroundColor: '#ffffff',
                mb: 4,
                width: '100%'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold">Analysis Controls</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel id="timeframe-select-label">Timeframe</InputLabel>
                      <Select
                        labelId="timeframe-select-label"
                        id="timeframe-select"
                        value={timeframe}
                        onChange={handleTimeframeChange}
                        label="Timeframe"
                        sx={{ 
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          }
                        }}
                      >
                        <MenuItem value="1w">Last Week</MenuItem>
                        <MenuItem value="1m">Last Month</MenuItem>
                        <MenuItem value="3m">Last 3 Months</MenuItem>
                        <MenuItem value="6m">Last 6 Months</MenuItem>
                        <MenuItem value="1y">Last Year</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  
                  <Button
                    variant="contained"
                    disableElevation
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={loading}
                    sx={{ 
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 'medium',
                      boxShadow: 'none',
                      background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                      '&:hover': {
                        boxShadow: theme.shadows[2]
                      }
                    }}
                  >
                    {loading ? 'Refreshing...' : 'Refresh Data'}
                  </Button>
                </Box>
              </Box>
            </Paper>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress color="primary" size={60} thickness={4} />
              </Box>
            ) : error ? (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3, 
                  borderRadius: 2,
                  boxShadow: theme.shadows[3]
                }}
              >
                {error}
              </Alert>
            ) : success ? (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 3, 
                  borderRadius: 2,
                  boxShadow: theme.shadows[3]
                }}
              >
                {success}
              </Alert>
            ) : (
              <>
                {/* Metrics Overview Cards */}
                <Paper 
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    boxShadow: theme.shadows[2],
                    backgroundColor: '#ffffff',
                    mb: 4
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>Membership Summary</Typography>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={3}>
                    {/* Total Members Card */}
                    <Grid item xs={6} sm={6} md={3}>
                      <Paper sx={cardStyles.totalMembers.cardStyle}>
                        {cardStyles.totalMembers.icon && 
                          <Box component="span" sx={cardStyles.totalMembers.iconStyle}>
                            {cardStyles.totalMembers.icon}
                          </Box>
                        }
                        <Box sx={cardStyles.totalMembers.gradientOverlay} />
                        <Typography variant="subtitle2" fontWeight="medium" color="text.secondary">Total Members</Typography>
                        <Typography variant="h3" sx={{ my: 1, fontWeight: 'bold' }}>
                          {(analyticsData.metrics.totalMembers || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          All-time registered members
                        </Typography>
                      </Paper>
                    </Grid>
                    
                    {/* Active Members Card */}
                    <Grid item xs={6} sm={6} md={3}>
                      <Paper sx={cardStyles.activeMembers.cardStyle}>
                        {cardStyles.activeMembers.icon && 
                          <Box component="span" sx={cardStyles.activeMembers.iconStyle}>
                            {cardStyles.activeMembers.icon}
                          </Box>
                        }
                        <Box sx={cardStyles.activeMembers.gradientOverlay} />
                        <Typography variant="subtitle2" fontWeight="medium" color="text.secondary">Active Members</Typography>
                        <Typography variant="h3" sx={{ my: 1, fontWeight: 'bold' }}>
                          {(analyticsData.metrics.activeMembers || 0).toLocaleString()}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" color="success.main" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center' }}>
                            {Math.round(((analyticsData.metrics.activeMembers || 0) / (analyticsData.metrics.totalMembers || 1)) * 100)}% 
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            engagement rate
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                    
                    {/* New Members Card */}
                    <Grid item xs={6} sm={6} md={3}>
                      <Paper sx={cardStyles.newMembers.cardStyle}>
                        {cardStyles.newMembers.icon && 
                          <Box component="span" sx={cardStyles.newMembers.iconStyle}>
                            {cardStyles.newMembers.icon}
                          </Box>
                        }
                        <Box sx={cardStyles.newMembers.gradientOverlay} />
                        <Typography variant="subtitle2" fontWeight="medium" color="text.secondary">New Members</Typography>
                        <Typography variant="h3" sx={{ my: 1, fontWeight: 'bold' }}>
                          {(analyticsData.metrics.newMembers || 0).toLocaleString()}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography 
                            variant="body2" 
                            color={(analyticsData.metrics.growthRate || 0) >= 0 ? 'success.main' : 'error.main'} 
                            fontWeight="medium"
                            sx={{ display: 'flex', alignItems: 'center' }}
                          >
                            {(analyticsData.metrics.growthRate || 0) >= 0 ? (
                              <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} />
                            ) : (
                              <TrendingDownIcon fontSize="small" sx={{ mr: 0.5 }} />
                            )}
                            {(analyticsData.metrics.growthRate !== undefined && analyticsData.metrics.growthRate !== null) 
                              ? `${(analyticsData.metrics.growthRate >= 0 ? '+' : '')}${analyticsData.metrics.growthRate}%` 
                              : '0%'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            {timeframe === '1w' ? 'this week' : 
                             timeframe === '1m' ? 'this month' : 
                             timeframe === '3m' ? 'last 3 months' : 
                             timeframe === '6m' ? 'last 6 months' : 'this year'}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                    
                    {/* Churn Rate Card - Enhanced with industry metrics */}
                    <Grid item xs={6} sm={6} md={3}>
                      <Paper sx={cardStyles.churnRate.cardStyle}>
                        {cardStyles.churnRate.icon && 
                          <Box component="span" sx={cardStyles.churnRate.iconStyle}>
                            {cardStyles.churnRate.icon}
                          </Box>
                        }
                        <Box sx={cardStyles.churnRate.gradientOverlay} />
                        <Typography variant="subtitle2" fontWeight="medium" color="text.secondary">Churn Rate</Typography>
                        <Typography variant="h3" sx={{ my: 1, fontWeight: 'bold' }}>
                          {(analyticsData.metrics.churnRate || 0).toFixed(2)}%
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          {(analyticsData.metrics.churnRate || 0) > 5 ? (
                            <Typography variant="body2" color="error.main" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center' }}>
                              <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} />
                              High risk
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="success.main" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center' }}>
                              <TrendingDownIcon fontSize="small" sx={{ mr: 0.5 }} />
                              Healthy
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            compared to industry avg.
                          </Typography>
                        </Box>
                        <Box 
                          sx={{ 
                            mt: 0.5, 
                            p: 1, 
                            borderRadius: 1, 
                            bgcolor: alpha(theme.palette.background.default, 0.5),
                            fontSize: '0.75rem'
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Industry benchmark: 
                            <Box component="span" fontWeight="bold" sx={{ ml: 0.5 }}>
                              {getIndustryCategory(analyticsData.metrics.churnRate || 0)}
                            </Box>
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Paper>
                
                {/* Charts Section */}
                <Paper 
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    boxShadow: theme.shadows[2],
                    backgroundColor: '#ffffff',
                    mb: 4
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>Membership Insights</Typography>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={3}>
                    {/* Member Growth Trend */}
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>Membership Growth Trend</Typography>
                        <Box sx={{ height: 350, p: 1 }}>
                          {analyticsData.growth.length > 0 ? (
                            <Line 
                              data={growthChartData}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                  legend: {
                                    position: 'top',
                                    align: 'end',
                                    labels: {
                                      usePointStyle: true,
                                      boxWidth: 8,
                                      padding: 20
                                    }
                                  },
                                  tooltip: {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.9),
                                    titleFont: {
                                      size: 14,
                                      weight: 'bold'
                                    },
                                    bodyFont: {
                                      size: 13
                                    },
                                    padding: 12,
                                    cornerRadius: 8,
                                    boxPadding: 6
                                  }
                                },
                                scales: {
                                  x: {
                                    grid: {
                                      display: false
                                    },
                                    ticks: {
                                      font: {
                                        size: 11
                                      }
                                    }
                                  },
                                  y: {
                                    grid: {
                                      color: alpha(theme.palette.text.primary, 0.05),
                                      drawBorder: false
                                    },
                                    ticks: {
                                      font: {
                                        size: 11
                                      }
                                    }
                                  }
                                },
                                elements: {
                                  point: {
                                    radius: 4,
                                    hoverRadius: 6
                                  },
                                  line: {
                                    tension: 0.4
                                  }
                                },
                                interaction: {
                                  mode: 'index',
                                  intersect: false
                                }
                              }}
                            />
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                              <Typography color="text.secondary" variant="subtitle1">No growth data available</Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                    
                    {/* Membership Distribution */}
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>Membership Distribution</Typography>
                        <Box sx={{ height: 350, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          {analyticsData.distribution && analyticsData.distribution.length > 0 ? (
                            <Doughnut 
                              data={distributionChartData}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                cutout: '65%',
                                plugins: {
                                  legend: {
                                    position: 'bottom',
                                    display: true,
                                    labels: {
                                      color: theme.palette.text.primary,
                                      usePointStyle: true,
                                      padding: 15,
                                      font: {
                                        size: 11,
                                        weight: 'medium'
                                      }
                                    }
                                  },
                                  tooltip: {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.9),
                                    titleFont: {
                                      size: 14,
                                      weight: 'bold'
                                    },
                                    bodyFont: {
                                      size: 13
                                    },
                                    padding: 12,
                                    cornerRadius: 8,
                                    callbacks: {
                                      label: function(context) {
                                        const label = context.label || '';
                                        const value = context.raw || 0;
                                        const total = context.chart.data.datasets[0].data.reduce(
                                          (a, b) => a + b, 0
                                        );
                                        const percentage = Math.round((value / total) * 100);
                                        return `${label}: ${value} (${percentage}%)`;
                                      }
                                    }
                                  }
                                },
                                animation: {
                                  animateRotate: true,
                                  animateScale: true
                                }
                              }}
                            />
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                              <Typography color="text.secondary" variant="subtitle1">No distribution data available</Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
                
                {/* Retention Rate */}
                <Grid item xs={12}>
                    <Paper 
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        boxShadow: theme.shadows[2],
                        backgroundColor: '#ffffff'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" fontWeight="bold">Member Retention Rate</Typography>
                        <Typography 
                          variant="h5" 
                          fontWeight="bold" 
                          sx={{
                            color: analyticsData.retentionRate >= 80 ? theme.palette.success.main : 
                                  analyticsData.retentionRate >= 60 ? theme.palette.warning.main : theme.palette.error.main,
                            py: 0.5,
                            px: 2,
                            borderRadius: 2,
                            backgroundColor: analyticsData.retentionRate >= 80 ? alpha(theme.palette.success.main, 0.1) : 
                                          analyticsData.retentionRate >= 60 ? alpha(theme.palette.warning.main, 0.1) : alpha(theme.palette.error.main, 0.1)
                          }}
                        >
                          {analyticsData.retentionRate}%
                        </Typography>
                      </Box>
                      
                      <Box 
                        sx={{ 
                          width: '100%',
                          height: 12,
                          bgcolor: alpha(theme.palette.grey[300], 0.3),
                          borderRadius: 6,
                          overflow: 'hidden',
                          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
                        }}
                      >
                        <Box
                          sx={{
                            width: `${analyticsData.retentionRate}%`,
                            height: '100%',
                            borderRadius: 6,
                            transition: 'width 1s ease-in-out',
                            background: analyticsData.retentionRate >= 80 
                              ? `linear-gradient(90deg, ${alpha(theme.palette.success.light, 0.7)}, ${theme.palette.success.main})` 
                              : analyticsData.retentionRate >= 60 
                                ? `linear-gradient(90deg, ${alpha(theme.palette.warning.light, 0.7)}, ${theme.palette.warning.main})` 
                                : `linear-gradient(90deg, ${alpha(theme.palette.error.light, 0.7)}, ${theme.palette.error.main})`,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                          }}
                        />
                      </Box>
                      
                      <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12} md={8}>
                          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                            {analyticsData.retentionRate >= 80 ? 
                              'Excellent retention rate! Your members are highly engaged and satisfied with your services.' : 
                             analyticsData.retentionRate >= 60 ? 
                              'Good retention rate, but there\'s room for improvement. Consider enhancing member engagement strategies.' : 
                              'Low retention rate. Implement urgent strategies to improve member engagement and satisfaction. Conduct exit surveys to understand departure reasons.'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ 
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.background.default, 0.5),
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                          }}>
                            <Typography variant="subtitle2" fontWeight="medium" color="text.secondary" gutterBottom>
                              Industry Benchmarks
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" color="text.secondary">Poor</Typography>
                              <Typography variant="body2" fontWeight="medium">Below 60%</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" color="text.secondary">Average</Typography>
                              <Typography variant="body2" fontWeight="medium">60% - 80%</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">Excellent</Typography>
                              <Typography variant="body2" fontWeight="medium">Above 80%</Typography>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                {/* Churn Analysis Section */}
                <Paper 
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    boxShadow: theme.shadows[2],
                    backgroundColor: '#ffffff',
                    mb: 4
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold">Churn Rate Analysis</Typography>
                    <Chip 
                      label={analyticsData.metrics.churnRate > 15 ? "High Risk" : 
                             analyticsData.metrics.churnRate > 10 ? "Moderate Risk" : 
                             analyticsData.metrics.churnRate > 5 ? "Low Risk" : "Healthy"}
                      color={analyticsData.metrics.churnRate > 15 ? "error" : 
                             analyticsData.metrics.churnRate > 10 ? "warning" : 
                             analyticsData.metrics.churnRate > 5 ? "info" : "success"}
                      sx={{ px: 1, fontSize: '0.9rem' }}
                    />
                  </Box>
                  
                  <Divider sx={{ mb: 3 }} />
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                        Current Churn Rate Impact
                      </Typography>
                      
                      {/* Calculate metrics based on current churn rate */}
                      {(() => {
                        const churnMetrics = calculateChurnMetrics(analyticsData.metrics.churnRate || 0);
                        return (
                          <>
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                At your current churn rate of <b>{(analyticsData.metrics.churnRate || 0).toFixed(2)}%</b>, your organization is experiencing:
                              </Typography>
                              
                              <Box sx={{ mt: 2 }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6}>
                                    <Paper 
                                      elevation={0}
                                      sx={{ 
                                        p: 2, 
                                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                                        borderRadius: 2,
                                        height: '100%'
                                      }}
                                    >
                                      <Typography variant="subtitle2" color="primary.main" gutterBottom>
                                        Membership Lifetime
                                      </Typography>
                                      <Typography variant="h4" fontWeight="bold" color="primary.dark">
                                        {churnMetrics.avgLifetimeMonths.toFixed(1)} months
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Average length members stay before churning
                                      </Typography>
                                    </Paper>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Paper 
                                      elevation={0}
                                      sx={{ 
                                        p: 2, 
                                        bgcolor: alpha(theme.palette.error.main, 0.05),
                                        borderRadius: 2,
                                        height: '100%'
                                      }}
                                    >
                                      <Typography variant="subtitle2" color="error.main" gutterBottom>
                                        Revenue Impact
                                      </Typography>
                                      <Typography variant="h4" fontWeight="bold" color="error.dark">
                                        {churnMetrics.revenueImpact}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Estimated annual revenue lost due to churn
                                      </Typography>
                                    </Paper>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Box>
                            
                            <Typography variant="subtitle1" fontWeight="medium" gutterBottom sx={{ mt: 3 }}>
                              Improvement Opportunity
                            </Typography>
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="body2" gutterBottom>
                                Reducing your churn rate to <b>{churnMetrics.improvement.target}%</b> could:
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1, flexWrap: 'wrap' }}>
                                <Chip 
                                  color="success" 
                                  variant="outlined"
                                  label={`Increase membership lifetime by ${((1 / (churnMetrics.improvement.target / 100)) - churnMetrics.avgLifetimeMonths).toFixed(1)} months`}
                                />
                                <Chip 
                                  color="success" 
                                  variant="outlined"
                                  label={`Recover ~${churnMetrics.improvement.potential}% in potential revenue`}
                                />
                              </Box>
                            </Box>
                          </>
                        );
                      })()}
                    </Grid>
                    
                    <Grid item xs={12} md={5}>
                      <Box sx={{ p: 2, bgcolor: alpha(theme.palette.background.default, 0.4), borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                          Industry Comparison
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          Based on membership organization industry benchmarks:
                        </Typography>
                        
                        <Box sx={{ mt: 3, mb: 1 }}>
                          {/* Industry benchmark progress bars */}
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2">Excellent</Typography>
                              <Typography variant="body2" fontWeight="medium">Below 5%</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Box sx={{ 
                                height: 8, 
                                width: '70%', 
                                bgcolor: theme.palette.success.main, 
                                borderRadius: 4 
                              }} />
                              <Box sx={{ 
                                height: 8, 
                                width: '30%', 
                                bgcolor: alpha(theme.palette.success.main, 0.3), 
                                borderRadius: '0 4px 4px 0' 
                              }} />
                            </Box>
                          </Box>
                          
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2">Good</Typography>
                              <Typography variant="body2" fontWeight="medium">5-10%</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Box sx={{ 
                                height: 8, 
                                width: '50%', 
                                bgcolor: theme.palette.info.main, 
                                borderRadius: 4 
                              }} />
                              <Box sx={{ 
                                height: 8, 
                                width: '50%', 
                                bgcolor: alpha(theme.palette.info.main, 0.3), 
                                borderRadius: '0 4px 4px 0' 
                              }} />
                            </Box>
                          </Box>
                          
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2">Average</Typography>
                              <Typography variant="body2" fontWeight="medium">10-15%</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Box sx={{ 
                                height: 8, 
                                width: '60%', 
                                bgcolor: theme.palette.warning.main, 
                                borderRadius: 4 
                              }} />
                              <Box sx={{ 
                                height: 8, 
                                width: '40%', 
                                bgcolor: alpha(theme.palette.warning.main, 0.3), 
                                borderRadius: '0 4px 4px 0' 
                              }} />
                            </Box>
                          </Box>
                          
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2">Poor</Typography>
                              <Typography variant="body2" fontWeight="medium">Above 15%</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Box sx={{ 
                                height: 8, 
                                width: '40%', 
                                bgcolor: theme.palette.error.main, 
                                borderRadius: 4 
                              }} />
                              <Box sx={{ 
                                height: 8, 
                                width: '60%', 
                                bgcolor: alpha(theme.palette.error.main, 0.3), 
                                borderRadius: '0 4px 4px 0' 
                              }} />
                            </Box>
                          </Box>
                        </Box>
                        
                        <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.background.default, 0.7), borderRadius: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Your Position
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Your churn rate of <b>{analyticsData.metrics.churnRate || 0}%</b> places your organization in the 
                            <b> {
                              (analyticsData.metrics.churnRate || 0) <= 5 ? 'excellent' :
                              (analyticsData.metrics.churnRate || 0) <= 10 ? 'good' :
                              (analyticsData.metrics.churnRate || 0) <= 15 ? 'average' : 'poor'
                            } </b> 
                            category compared to industry standards.
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    Common Churn Factors & Recommendations
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper 
                        elevation={0}
                        sx={{ 
                          p: 2, 
                          bgcolor: alpha(theme.palette.background.default, 0.4),
                          borderRadius: 2,
                          height: '100%',
                          borderLeft: `4px solid ${theme.palette.primary.main}`
                        }}
                      >
                        <Typography variant="subtitle2" color="primary.main" gutterBottom>
                          Onboarding Experience
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Improve member onboarding with personalized welcome journeys, introductory calls, and comprehensive setup guidance.
                        </Typography>
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper 
                        elevation={0}
                        sx={{ 
                          p: 2, 
                          bgcolor: alpha(theme.palette.background.default, 0.4),
                          borderRadius: 2,
                          height: '100%',
                          borderLeft: `4px solid ${theme.palette.secondary.main}`
                        }}
                      >
                        <Typography variant="subtitle2" color="secondary.main" gutterBottom>
                          Engagement Opportunities
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Create regular touchpoints through newsletters, events, webinars, and member recognition programs to maintain connection.
                        </Typography>
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper 
                        elevation={0}
                        sx={{ 
                          p: 2, 
                          bgcolor: alpha(theme.palette.background.default, 0.4),
                          borderRadius: 2,
                          height: '100%',
                          borderLeft: `4px solid ${theme.palette.success.main}`
                        }}
                      >
                        <Typography variant="subtitle2" color="success.main" gutterBottom>
                          Exit Surveys
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Implement exit surveys to collect direct feedback from departing members and identify specific improvement areas.
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Expired Member Churn Analysis */}
                <Paper 
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    boxShadow: theme.shadows[2],
                    backgroundColor: '#ffffff',
                    mb: 4
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold">Expired Member Analysis</Typography>
                    <Chip 
                      label={analyticsData.metrics.expiredMembers > 0 ? `${analyticsData.metrics.expiredMembers} Expired Members` : "No Expired Members"}
                      color={analyticsData.metrics.expiredMembers > 10 ? "error" : 
                             analyticsData.metrics.expiredMembers > 5 ? "warning" : "success"}
                      sx={{ px: 1, fontSize: '0.9rem' }}
                    />
                  </Box>
                  
                  <Divider sx={{ mb: 3 }} />
                  
                  {/* Calculate and display enhanced churn metrics that include expired members */}
                  {(() => {
                    const enhancedMetrics = calculateEnhancedChurnMetrics(analyticsData.metrics);
                    return (
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={7}>
                          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                            Expired Member Impact
                          </Typography>
                          
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" paragraph>
                              Expired memberships are treated as churned customers in this analysis. This provides a more accurate 
                              view of your actual member retention performance.
                            </Typography>
                            
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                              <Grid item xs={12} sm={6}>
                                <Paper 
                                  elevation={0} 
                                  sx={{ 
                                    p: 2, 
                                    bgcolor: alpha(theme.palette.error.main, 0.05),
                                    borderRadius: 2,
                                    height: '100%'
                                  }}
                                >
                                  <Typography variant="subtitle2" color="error.main" gutterBottom>
                                    Calculated Churn Rate
                                  </Typography>
                                  <Typography variant="h4" fontWeight="bold" color="error.dark">
                                    {enhancedMetrics.calculatedChurnRate.toFixed(1)}%
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Based on expired members / total members
                                  </Typography>
                                </Paper>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Paper 
                                  elevation={0} 
                                  sx={{ 
                                    p: 2, 
                                    bgcolor: alpha(theme.palette.warning.main, 0.05),
                                    borderRadius: 2,
                                    height: '100%'
                                  }}
                                >
                                  <Typography variant="subtitle2" color="warning.main" gutterBottom>
                                    Cost of Churn
                                  </Typography>
                                  <Typography variant="h4" fontWeight="bold" color="warning.dark">
                                    ${enhancedMetrics.costOfChurn.toLocaleString()}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Estimated acquisition cost to replace churned members
                                  </Typography>
                                </Paper>
                              </Grid>
                            </Grid>
                          </Box>
                          
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                              Net Member Growth
                            </Typography>
                            
                            <Paper 
                              elevation={0} 
                              sx={{ 
                                p: 2,
                                bgcolor: alpha(enhancedMetrics.netMemberGrowth >= 0 ? theme.palette.success.main : theme.palette.error.main, 0.05),
                                borderRadius: 2,
                                borderLeft: `4px solid ${enhancedMetrics.netMemberGrowth >= 0 ? theme.palette.success.main : theme.palette.error.main}`
                              }}
                            >
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={8}>
                                  <Typography variant="body2" color="text.secondary">
                                    Your organization has {enhancedMetrics.netMemberGrowth >= 0 ? 'gained' : 'lost'} <b>{Math.abs(enhancedMetrics.netMemberGrowth)}</b> net {Math.abs(enhancedMetrics.netMemberGrowth) === 1 ? 'member' : 'members'} during this period, 
                                    resulting in a net growth rate of <b>{enhancedMetrics.netGrowthRate.toFixed(1)}%</b>.
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    <b>{analyticsData.metrics.newMembers}</b> new members joined while <b>{analyticsData.metrics.expiredMembers}</b> members expired or churned.
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
                                  <Typography 
                                    variant="h4" 
                                    fontWeight="bold" 
                                    color={enhancedMetrics.netMemberGrowth >= 0 ? "success.main" : "error.main"}
                                    sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'center' 
                                    }}
                                  >
                                    {enhancedMetrics.netMemberGrowth >= 0 ? '+' : ''}{enhancedMetrics.netMemberGrowth}
                                    {enhancedMetrics.netMemberGrowth >= 0 ? 
                                      <TrendingUpIcon fontSize="large" sx={{ ml: 1 }} /> : 
                                      <TrendingDownIcon fontSize="large" sx={{ ml: 1 }} />
                                    }
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Paper>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12} md={5}>
                          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                            Member Composition
                          </Typography>
                          
                          <Box sx={{ p: 2, bgcolor: alpha(theme.palette.background.default, 0.4), borderRadius: 2 }}>
                            {/* Member Ratio Visualization - Use actual expired member percentage */}
                            <Box sx={{ mb: 3 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">Active Members</Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {((analyticsData.metrics.activeMembers / analyticsData.metrics.totalMembers) * 100).toFixed(1)}%
                                </Typography>
                              </Box>
                              <Box sx={{ 
                                height: 16, 
                                width: '100%', 
                                bgcolor: alpha(theme.palette.grey[200], 0.5), 
                                borderRadius: 8,
                                overflow: 'hidden'
                              }}>
                                <Box sx={{ 
                                  height: '100%', 
                                  width: `${(analyticsData.metrics.activeMembers / analyticsData.metrics.totalMembers) * 100}%`, 
                                  bgcolor: theme.palette.success.main,
                                  borderRadius: 8
                                }} />
                              </Box>
                            </Box>
                            
                            <Box sx={{ mb: 3 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">Expired/Churned Members</Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {analyticsData.metrics.totalMembers && analyticsData.metrics.expiredMembers 
                                    ? ((analyticsData.metrics.expiredMembers / analyticsData.metrics.totalMembers) * 100).toFixed(1) 
                                    : '0.0'}%
                                </Typography>
                              </Box>
                              <Box sx={{ 
                                height: 16, 
                                width: '100%', 
                                bgcolor: alpha(theme.palette.grey[200], 0.5), 
                                borderRadius: 8,
                                overflow: 'hidden'
                              }}>
                                <Box sx={{ 
                                  height: '100%', 
                                  width: `${analyticsData.metrics.totalMembers && analyticsData.metrics.expiredMembers 
                                    ? (analyticsData.metrics.expiredMembers / analyticsData.metrics.totalMembers) * 100 
                                    : 0}%`, 
                                  bgcolor: theme.palette.error.main,
                                  borderRadius: 8
                                }} />
                              </Box>
                            </Box>
                            
                            {/* Add a section for other member statuses if the total doesn't add up to 100% */}
                            {analyticsData.metrics.activeMembers + analyticsData.metrics.expiredMembers < analyticsData.metrics.totalMembers && (
                              <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="body2">Other Statuses</Typography>
                                  <Typography variant="body2" fontWeight="medium">
                                    {(((analyticsData.metrics.totalMembers - analyticsData.metrics.activeMembers - analyticsData.metrics.expiredMembers) / analyticsData.metrics.totalMembers) * 100).toFixed(1)}%
                                  </Typography>
                                </Box>
                                <Box sx={{ 
                                  height: 16, 
                                  width: '100%', 
                                  bgcolor: alpha(theme.palette.grey[200], 0.5), 
                                  borderRadius: 8,
                                  overflow: 'hidden'
                                }}>
                                  <Box sx={{ 
                                    height: '100%', 
                                    width: `${((analyticsData.metrics.totalMembers - analyticsData.metrics.activeMembers - analyticsData.metrics.expiredMembers) / analyticsData.metrics.totalMembers) * 100}%`, 
                                    bgcolor: theme.palette.info.main,
                                    borderRadius: 8
                                  }} />
                                </Box>
                              </Box>
                            )}
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Typography variant="subtitle2" gutterBottom>
                              Retention Strategies for Expired Members
                            </Typography>
                            
                            <Box sx={{ mt: 2 }}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'flex-start', 
                                mb: 1.5,
                                gap: 1.5
                              }}>
                                <Chip size="small" label="1" color="primary" />
                                <Typography variant="body2" color="text.secondary">
                                  Send targeted re-engagement emails to recently expired members with special renewal offers.
                                </Typography>
                              </Box>
                              
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'flex-start', 
                                mb: 1.5,
                                gap: 1.5
                              }}>
                                <Chip size="small" label="2" color="primary" />
                                <Typography variant="body2" color="text.secondary">
                                  Implement a "win-back" campaign with personalized incentives based on previous engagement.
                                </Typography>
                              </Box>
                              
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'flex-start',
                                gap: 1.5
                              }}>
                                <Chip size="small" label="3" color="primary" />
                                <Typography variant="body2" color="text.secondary">
                                  Create an exit survey specifically for expired memberships to identify improvement opportunities.
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    );
                  })()}
                </Paper>
              </>
            )}
          </Box>
        </Fade>
        
      </Box>
      
      {/* Print Menu */}
      <Menu
        anchorEl={printMenuAnchor}
        open={Boolean(printMenuAnchor)}
        onClose={handlePrintMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { mt: 1, minWidth: 200, borderRadius: 2 }
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
          Select Report Type
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <MenuItem onClick={() => handleSelectPrintReport('all')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#3f51b5' }} />
          Complete Analytics Report
        </MenuItem>
        <MenuItem onClick={() => handleSelectPrintReport('growth')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#1565c0' }} />
          Growth Trend Only
        </MenuItem>
        <MenuItem onClick={() => handleSelectPrintReport('distribution')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#9c27b0' }} />
          Distribution Data Only
        </MenuItem>
        <MenuItem onClick={() => handleSelectPrintReport('retention')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#e65100' }} />
          Retention & Churn Only
        </MenuItem>
      </Menu>
      
      {/* Print Preview Dialog */}
      <Dialog
        open={printDialogOpen}
        onClose={() => !isPrinting && setPrintDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'primary.light',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PrintIcon sx={{ mr: 1 }} />
            Membership Analytics Report
          </Box>
          {!isPrinting && (
            <IconButton
              onClick={() => setPrintDialogOpen(false)}
              sx={{ color: 'white' }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: '#f5f5f5',
          minHeight: '70vh'
        }}>
          <Box sx={{ my: 2, width: '100%', overflow: 'auto' }}>
            <MembershipAnalyticsReport
              ref={analyticsReportRef}
              analyticsData={analyticsData}
              timeframe={timeframe}
              reportType={printReportType}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Button
            onClick={() => setPrintDialogOpen(false)}
            disabled={isPrinting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePrintReport}
            disabled={isPrinting}
            variant="contained"
            startIcon={isPrinting ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
            color="primary"
          >
            {isPrinting ? 'Generating PDF...' : 'Download PDF Report'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Printing Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isPrinting}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          p: 4,
          borderRadius: 2
        }}>
          <CircularProgress color="inherit" sx={{ mb: 2 }} />
          <Typography variant="h6">
            Generating PDF...
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Please wait, this may take a moment
          </Typography>
        </Box>
      </Backdrop>
    </Container>
  );
};

// MembershipAnalyticsReport component for the PDF export
const MembershipAnalyticsReport = forwardRef(({ analyticsData, timeframe, reportType = 'all' }, ref) => {
  // Generate formatted report date
  const reportDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Helper function for percent calculation with safety checks
  const calculatePercentage = (part, total) => {
    if (!total) return 0;
    return ((part / total) * 100).toFixed(1);
  };

  return (
    <Box ref={ref} sx={{ 
      p: 4, 
      width: '210mm',
      minHeight: '297mm',
      backgroundColor: 'white',
      color: 'black',
      fontFamily: 'Arial, sans-serif',
      fontSize: '10pt'
    }}>
      {/* Report Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: '#3f51b5' }}>
          Membership Analytics Report
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
          Comprehensive Membership Performance Analysis
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Generated on {reportDate}
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Membership Summary */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#3f51b5', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
          Membership Summary
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#e8eaf6', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Total Members
              </Typography>
              <Typography variant="h5" sx={{ color: '#3f51b5', fontWeight: 'bold' }}>
                {(analyticsData.metrics.totalMembers || 0).toLocaleString()}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Active Members
              </Typography>
              <Typography variant="h5" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                {(analyticsData.metrics.activeMembers || 0).toLocaleString()}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                New Members
              </Typography>
              <Typography variant="h5" sx={{ color: '#1565c0', fontWeight: 'bold' }}>
                {(analyticsData.metrics.newMembers || 0).toLocaleString()}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Churn Rate
              </Typography>
              <Typography variant="h5" sx={{ color: '#c62828', fontWeight: 'bold' }}>
                {analyticsData.metrics.churnRate || 0}%
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Membership Growth Trend - only show if reportType is all or growth */}
      {(reportType === 'all' || reportType === 'growth') && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1565c0', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Membership Growth Trend
          </Typography>
          
          <Table sx={{ mb: 2 }}>
            <TableHead sx={{ bgcolor: '#e3f2fd' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Time Period</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Members</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Growth</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {analyticsData.growth.map((item, index, array) => {
                // Calculate growth percentage
                const prevValue = index > 0 ? array[index - 1].members : item.members;
                const growthPercent = prevValue ? ((item.members - prevValue) / prevValue * 100).toFixed(1) : 0;
                
                return (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f5f5f5' } }}>
                    <TableCell>{item.month}</TableCell>
                    <TableCell>{item.members}</TableCell>
                    <TableCell sx={{ 
                      color: growthPercent > 0 ? '#2e7d32' : growthPercent < 0 ? '#c62828' : 'text.secondary',
                      fontWeight: 'medium'
                    }}>
                      {growthPercent > 0 ? '+' : ''}{growthPercent}%
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            Time frame: {
              timeframe === '1w' ? 'Last Week' :
              timeframe === '1m' ? 'Last Month' :
              timeframe === '3m' ? 'Last 3 Months' :
              timeframe === '6m' ? 'Last 6 Months' : 'Last Year'
            }
          </Typography>
        </Box>
      )}

      {/* Membership Distribution - only show if reportType is all or distribution */}
      {(reportType === 'all' || reportType === 'distribution') && analyticsData.distribution && analyticsData.distribution.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#9c27b0', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Membership Distribution
          </Typography>
          
          <Table sx={{ mb: 2 }}>
            <TableHead sx={{ bgcolor: '#f3e5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Members</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Percentage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {analyticsData.distribution.map((item, index) => {
                const totalMembers = analyticsData.distribution.reduce((sum, item) => sum + item.count, 0);
                const percentage = calculatePercentage(item.count, totalMembers);
                
                return (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f5f5f5' } }}>
                    <TableCell>{item.category}</TableCell>
                    <TableCell>{item.count}</TableCell>
                    <TableCell>{percentage}%</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Retention & Churn Analysis - only show if reportType is all or retention */}
      {(reportType === 'all' || reportType === 'retention') && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#e65100', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Retention & Churn Analysis
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>Retention Rate</Typography>
                <Typography variant="h4" sx={{ 
                  color: analyticsData.retentionRate >= 80 ? '#2e7d32' : 
                          analyticsData.retentionRate >= 60 ? '#ed6c02' : '#c62828',
                  fontWeight: 'bold',
                  mb: 1
                }}>
                  {analyticsData.retentionRate}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {analyticsData.retentionRate >= 80 ? 
                    'Excellent retention rate, well above industry average.' : 
                    analyticsData.retentionRate >= 60 ? 
                    'Good retention rate, but there is room for improvement.' : 
                    'Below average retention rate. Immediate action recommended.'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>Churn Analysis</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Churn Rate:</strong> {analyticsData.metrics.churnRate || 0}%
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Industry Classification:</strong> {
                    (analyticsData.metrics.churnRate || 0) <= 5 ? 'Excellent' :
                    (analyticsData.metrics.churnRate || 0) <= 10 ? 'Good' :
                    (analyticsData.metrics.churnRate || 0) <= 15 ? 'Average' : 'Poor'
                  }
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Expired Members:</strong> {analyticsData.metrics.expiredMembers || 0}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          This is an official membership analytics report generated from the organization's membership system.
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.disabled' }}>
          Report ID: {Math.random().toString(36).substr(2, 9).toUpperCase()} | Page 1
        </Typography>
      </Box>
    </Box>
  );
});

MembershipAnalyticsReport.displayName = 'MembershipAnalyticsReport';

export default MembershipAnalytics;
